import { Component, computed, effect, inject, Input, signal, viewChild, ViewChild, Signal, ChangeDetectionStrategy } from '@angular/core';
import { ClientListComponent } from '../matwins-manager/client/client-list/client-list.component';
import { ClientStackComponent } from '../matwins-manager/client/client-stack/client-stack.component';
import { PointOfServiceListComponent } from '../matwins-tenant/point-of-service/pos-list/point-of-service-list.component';
import { VisitListComponent } from '../matwins-tenant/visit/visit-list/visit-list.component';
import { AccessCardListComponent } from '../matwins-tenant/access-card/access-card-list/access-card-list.component';
import { DockListComponent } from '../matwins-tenant/dock/dock-list/dock-list.component';
import { TenantAccountListComponent } from '../matwins-tenant/account/account-list/account-list.component';
import { EmployeeListComponent } from '../matwins-tenant/employee/employee-list/employee-list.component';
import { DeliveryListComponent } from '../matwins-tenant/delivery/delivery-list/delivery-list.component';
import { BuzzerConfigListComponent } from '../matwins-tenant/buzzer-config/buzzer-config-list/buzzer-config-list.component';
import { Client } from '@app/common-codes/models/matwins-manager/Client.model';
import { PointOfService } from '@app/common-codes/models/matwins-tenant/PointOfService.model';
import { FeatureMediatorComponent } from '@app/common-codes/components/feature-med-components/FeatureMediatorComponent';
import { CurrentTenantService } from '@app/common-codes/services/CurrentTenantService';
import { DependencyInjectionProvider } from '@app/common-codes/services/DependecyInjection.provider';
import { MatwinsVariablesService } from '@app/common-codes/services/matwinsVariables.service';
import { LookupService } from '@app/common-codes/services/LookupService';
import { PermissionsService } from '@app/common-codes/services/Permissions.service';
import { EntityChangeLogService } from '@app/common-codes/services/EntityChangeLogService';
import { FilterStateService } from '@app/common-codes/services/FilterStateService';
import { StructuredDataRequest } from '@app/common-codes/_DTOs/IStructuredData';
import { Account } from '@app/common-codes/models/matwins-tenant/Account.model';
import { Employee } from '@app/common-codes/models/matwins-tenant/Employee.model';
import { Visit } from '@app/common-codes/models/matwins-tenant/Visit.model';
import { AccessCard } from '@app/common-codes/models/matwins-tenant/AccessCard.model';
import { Dock } from '@app/common-codes/models/matwins-tenant/Dock.model';
import { Delivery } from '@app/common-codes/models/matwins-tenant/Delivery.model';
import { BuzzerConfig } from '@app/common-codes/models/matwins-tenant/BuzzerConfig.model';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';

@Component({
  selector: 'app-visitor-feature',
  standalone: true,
  imports: [
    FeatureMediatorComponent,
    ClientListComponent,
    ClientStackComponent,
    PointOfServiceListComponent,
    VisitListComponent,
    AccessCardListComponent,
    TenantAccountListComponent,
    EmployeeListComponent,
    DeliveryListComponent,
    BuzzerConfigListComponent,
    TranslatePipe
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './visitor-feature.component.html'
})
export class VisitorFeatureComponent {
  @Input() shouldFetchLookupsOnRootSelect: boolean = false;

  @ViewChild(FeatureMediatorComponent) private mediator!: FeatureMediatorComponent<Client, PointOfService>;

  private clientList = viewChild(ClientListComponent);

  // Portee de la remise a zero des filtres, lue par l'en-tete de l'application.
  ngAfterViewInit(): void {
    this.filterStateService.activeListKeys.set(this.mediator.nestedListKeys());
    void this.restoreSavedSelection();
  }

  private changeLog = inject(EntityChangeLogService);
  private di = inject(DependencyInjectionProvider);
  protected filterStateService = inject(FilterStateService);

  // Le Mediator a besoin de cette liste pour déduire les entités et lookups
  public readonly nestedListTypes: any[] = [
    PointOfServiceListComponent,
    VisitListComponent,
    DeliveryListComponent,
    BuzzerConfigListComponent,
    AccessCardListComponent,
    DockListComponent,
    TenantAccountListComponent,
    EmployeeListComponent
  ];

  // === SIGNAL DU CLIENT SÉLECTIONNÉ (alimenté par onClientSelected) ===
  private _selectedClient = signal<Client | null>(null);

  // Onglet actif : 'visits' ou 'deliveries'
  selectedFeatureTab = signal<'visits' | 'deliveries' | 'buzzers'>('visits');

  // === COMPUTED TYPIÉS ===
  readonly selectedClient: Signal<Client | null> = computed(() => this._selectedClient());

  readonly pointOfServices: Signal<PointOfService[]> = computed(() => {
    const client = this._selectedClient();
    return client?.relatedTenant?.pointOfServices ?? [];
  });

  readonly selectedPOS: Signal<PointOfService | null> = computed(() => {
    const posId = this.mediator.selectedLevel1Id();
    queueMicrotask(() => this.varsService.setPosId(posId));
    if (!posId) return null;
    return this.pointOfServices().find(p => p.id === posId) ?? null;
  });

  // Helper pour dédoublonner par id
  private uniqueById<T extends { id: any }>(items: T[]): T[] {
    const seen = new Set();
    return items.filter(item => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }

  readonly visits: Signal<Visit[]> = computed(() => {
    const pos = this.selectedPOS();
    if (pos) return pos.visits ?? [];
    const allPos = this.pointOfServices();
    const all = allPos.flatMap(p => p.visits ?? []);
    return this.uniqueById(all);
  });

  readonly deliveries: Signal<Delivery[]> = computed(() => {
    const pos = this.selectedPOS();
    if (pos) return pos.deliveries ?? [];
    const allPos = this.pointOfServices();
    const all = allPos.flatMap(p => p.deliveries ?? []);
    return this.uniqueById(all);
  });

  readonly buzzerConfigs: Signal<BuzzerConfig[]> = computed(() => {
    const pos = this.selectedPOS();
    if (pos) return pos.buzzerConfigs ?? [];
    const allPos = this.pointOfServices();
    const all = allPos.flatMap(p => p.buzzerConfigs ?? []);
    return this.uniqueById(all);
  });

  readonly accessCards: Signal<AccessCard[]> = computed(() => {
    const pos = this.selectedPOS();
    if (pos) return pos.accessCards ?? [];
    const allPos = this.pointOfServices();
    const all = allPos.flatMap(p => p.accessCards ?? []);
    return this.uniqueById(all);
  });

  readonly docks: Signal<Dock[]> = computed(() => {
    const pos = this.selectedPOS();
    if (pos) return pos.docks ?? [];
    const allPos = this.pointOfServices();
    const all = allPos.flatMap(p => p.docks ?? []);
    return this.uniqueById(all);
  });

  readonly employees: Signal<Employee[]> = computed(() => {
    let employees = this._selectedClient()?.unattachedEmployees ?? [];
    const pos = this.selectedPOS();
    if (pos) {
      const unionEmpl = [...(pos.employees ?? [])];
      return this.uniqueById(unionEmpl);
    }
    const allPos = this.pointOfServices();
    const all = [...employees, ...allPos.flatMap(p => p.employees ?? [])];
    return this.uniqueById(all);
  });

  readonly accounts: Signal<Account[]> = computed(() => {
    let accounts = this._selectedClient()?.unattachedAccounts ?? [];
    const pos = this.selectedPOS();
    if (pos) return pos.accounts ?? [];
    const allPos = this.pointOfServices();
    const all = [...accounts, ...allPos.flatMap(p => p.accounts ?? [])] as Account[];
    return this.uniqueById(all);
  });

  // Fournisseur pour les points de service à partir d'un client
  public getPointOfServices = (client: Client) => client?.relatedTenant?.pointOfServices ?? [];

  // Fournisseurs pour les sous-listes
  public level1Providers = {
    visits: (pos: PointOfService) => pos.visits ?? [],
    deliveries: (pos: PointOfService) => pos.deliveries ?? [],
    buzzerConfigs: (pos: PointOfService) => pos.buzzerConfigs ?? [],
    accessCards: (pos: PointOfService) => pos.accessCards ?? [],
    docks: (pos: PointOfService) => pos.docks ?? [],
    accounts: (pos: PointOfService) => pos.accounts ?? [],
    employees: (pos: PointOfService) => pos.employees ?? []
  };

  constructor(
    private tenantService: CurrentTenantService,
    private varsService: MatwinsVariablesService,
    private permissionsService: PermissionsService
  ) {
    // Écoute du clear du ChangeLog pour recharger le root
    effect(() => {
      const cleared = this.changeLog.lastClearedAt();
      if (cleared) {
        this.reloadClientRoot();
      }
    });
  }

  // === RECHARGEMENT ROOT QUAND CHANGELOG PURGÉ ===
  async reloadClientRoot(): Promise<void> {
    console.log('[VisitorFeature] ChangeLog purgé -> rechargement root Client');

    const clientId = this.mediator.selectedRootId();
    if (!clientId) {
      console.warn('[VisitorFeature] Aucun client sélectionné pour le rechargement');
      return;
    }

    const clientDataService = this.di.getOrCreateDataService(Client);
    const request: StructuredDataRequest = {
      pageNumber: 1,
      pageSize: 100,
      search: '',
      sortBy: '',
      sortOrder: 'asc',
      requiredLookups: [],
      dateRange: [],
      featureContext: 'visitor',
      includedEntities: this.mediator?.entityNames() ?? [],
      // Les filtres restaures voyagent avec la requete primaire : le premier affichage
      // porte deja les filtres au lieu de les voir s'appliquer apres coup.
      filters: this.filterStateService.filtersFor(this.mediator?.nestedListKeys() ?? [])
    };

    try {
      const response = await clientDataService.getAll(request);
      const freshClient = response.items.find((c: Client) => c.id === clientId);
      if (freshClient) {
        this._selectedClient.set(freshClient);
      }

      this.mediator.needRefreshData.set({
        entityName: 'Client',
        columnName: '',
        filterType: 'refresh',
        value: {}
      });
    } catch (err) {
      console.error('[VisitorFeature] Erreur rechargement root', err);
    }
  }

  private async restoreSavedSelection(): Promise<void> {
    const savedClientId = this.varsService.clientId;
    if (!savedClientId) return;

    const clientDataService = this.di.getOrCreateDataService(Client);
    const request: StructuredDataRequest = {
      pageNumber: 1,
      pageSize: 100,
      search: '',
      sortBy: '',
      sortOrder: 'asc',
      requiredLookups: [],
      dateRange: [],
      featureContext: 'visitor',
      includedEntities: this.mediator?.entityNames() ?? [],
      filters: this.filterStateService.filtersFor(this.mediator?.nestedListKeys() ?? [])
    };

    try {
      const response = await clientDataService.getAll(request);
      const client = response.items.find((c: Client) => c.id === savedClientId);
      if (!client) return;
      const currentRootId = this.mediator?.selectedRootId();
      if (currentRootId && currentRootId !== client.id) return;
      if (!currentRootId) {
        this.onClientSelected(client);
      }
      this.clientList()?.listView()?.selectedId.set(client.id);
    } catch (err) {
      console.error('[VisitorFeature] Erreur restauration du client memorise', err);
    }
  }

  // === SÉLECTION D'UN CLIENT ===
  onClientSelected(client: Client | null) {
    this._selectedClient.set(client);
    this.mediator.selectRoot(client);

    const posItems = client?.relatedTenant?.pointOfServices ?? [];
    const savedPosId = client ? this.varsService.getSavedPosId(client.id) : null;
    const targetPos = posItems.find(p => p.id === savedPosId) ?? posItems.find(p => p.defaultPOS) ?? null;
    this.mediator.selectLevel1(targetPos);

    this.tenantService.currentClientId.set(client?.id || '');
    this.varsService.setClientId(client?.id || null);
    LookupService.StoragePage = client?.relatedTenant?.prefix || 'all';
    this.tenantService.currentTenant.set(client?.relatedTenant ?? null);

    const prefix = client?.relatedTenant?.prefix ?? '';
    this.varsService.tenantPrefix = prefix.replace('https://', '').split('.')[0];
    this.varsService.tenantPrefixChanged$.set(prefix);

    if (!this.shouldFetchLookupsOnRootSelect && client !== null) {
      this.fetchLookupsExplicitly(client?.prefix);
    }
  }

  // === RECHARGEMENT DES LOOKUPS ===
  public async fetchLookupsExplicitly(clientDomain: string | undefined): Promise<void> {
    if (this.mediator) {
      const allRequiredLookups = this.mediator.otherLookupProfiles();
      if (allRequiredLookups.length > 0) {
        const posDataService = this.di.getOrCreateDataService(PointOfService);
        await posDataService.getLookupsOnly(allRequiredLookups);
        console.log('[VisitorFeature] Lookups fetched explicitly:', allRequiredLookups);
      }
    }
  }

  // === PERMISSIONS ===
  public get RequiredClientPermissions(): string[] {
    return [`Permissions.Entities.Manager.Client.Read`];
  }
  public get HasRequiredClientPermissions(): boolean {
    return this.permissionsService.UserHasAllPermissions(this.RequiredClientPermissions);
  }
  public get RequiredTenantPermissions(): string[] {
    return [`Permissions.Entities.Tenant.Tenant.Read`];
  }
  public get HasRequiredTenantPermissions(): boolean {
    return this.permissionsService.UserHasAllPermissions(this.RequiredTenantPermissions);
  }
}
